/**
 * SurveyResultsModal Component
 * Modal for viewing aggregated survey results with tabs for post-session, midpoint, and endpoint.
 * Pattern matches SessionDetailModal.tsx
 */

import { useState, useEffect } from 'react';
import { Modal, Tabs } from '@mantine/core';
import { SurveyResultsModalHeader } from './SurveyResultsModalHeader';
import { SurveyResultsContent } from './SurveyResultsContent';
import styles from './SurveyResultsModal.module.css';

interface SurveyResultsModalProps {
  opened: boolean;
  onClose: () => void;
  cohortId: string;
  groupId?: string;
  groupName?: string;
  coachName?: string;
  initialTab?: 'post_session' | 'midpoint' | 'endpoint';
}

type SurveyTab = 'post_session' | 'midpoint' | 'endpoint';

const TAB_TO_FORM_TYPE: Record<SurveyTab, string> = {
  post_session: 'post_session',
  midpoint: 'midpoint',
  endpoint: 'endpoint',
};

export function SurveyResultsModal({
  opened,
  onClose,
  cohortId,
  groupId,
  groupName,
  coachName,
  initialTab,
}: SurveyResultsModalProps) {
  const [activeTab, setActiveTab] = useState<SurveyTab>(initialTab || 'post_session');

  useEffect(() => {
    if (opened) {
      setActiveTab(initialTab || 'post_session');
    }
  }, [opened, initialTab]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size={1120}
      padding={0}
      styles={{ body: { padding: 0 } }}
      withCloseButton={false}
    >
      <SurveyResultsModalHeader
        groupName={groupName}
        coachName={coachName}
        onClose={onClose}
      />

      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab((value as SurveyTab) || 'post_session')}
      >
        <Tabs.List px="md">
          <Tabs.Tab value="post_session">Post Session</Tabs.Tab>
          <Tabs.Tab value="midpoint">Midpoint</Tabs.Tab>
          <Tabs.Tab value="endpoint">Endpoint</Tabs.Tab>
        </Tabs.List>

        <div className={styles.content}>
          <Tabs.Panel value="post_session">
            {activeTab === 'post_session' && (
              <SurveyResultsContent
                cohortId={cohortId}
                formType={TAB_TO_FORM_TYPE.post_session}
                groupId={groupId}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="midpoint">
            {activeTab === 'midpoint' && (
              <SurveyResultsContent
                cohortId={cohortId}
                formType={TAB_TO_FORM_TYPE.midpoint}
                groupId={groupId}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="endpoint">
            {activeTab === 'endpoint' && (
              <SurveyResultsContent
                cohortId={cohortId}
                formType={TAB_TO_FORM_TYPE.endpoint}
                groupId={groupId}
              />
            )}
          </Tabs.Panel>
        </div>
      </Tabs>
    </Modal>
  );
}

export default SurveyResultsModal;
